import React, { useEffect, useState } from 'react';
import { getExtensionTokens, createExtensionToken, revokeExtensionToken } from './api';
import Spinner from './ui/Spinner';
import Alert from './ui/Alert';
import ConfirmDialog from './ui/ConfirmDialog';

const errText = (err, fallback) => {
  const d = err?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
};

/**
 * Tokens for the "Save to DAM" Chrome extension (extension/ in the repo).
 * The token is shown once; the backend keeps only its hash. A token works only
 * on the extension and product-library endpoints.
 */
export const ExtensionTokensPanel = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [label, setLabel] = useState('');
  const [created, setCreated] = useState(null);
  const [creating, setCreating] = useState(false);
  const [pendingRevoke, setPendingRevoke] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setTokens(await getExtensionTokens());
    } catch (err) {
      setError(errText(err, 'Failed to load extension tokens'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const data = await createExtensionToken(label.trim() || 'Chrome extension');
      setCreated(data.token);
      setLabel('');
      await load();
    } catch (err) {
      setError(errText(err, 'Failed to create token'));
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async () => {
    const t = pendingRevoke;
    setPendingRevoke(null);
    if (!t) return;
    try {
      await revokeExtensionToken(t.id);
      setTokens((prev) => prev.filter((x) => x.id !== t.id));
    } catch (err) {
      setError(errText(err, 'Failed to revoke token'));
    }
  };

  const copy = (text) => {
    // Clipboard needs a secure context; over plain http the token is still selectable.
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => {});
    }
  };

  if (loading) return <Spinner label="Loading extension tokens…" />;

  return (
    <div>
      <p className="muted" style={{ margin: '0 0 12px' }}>
        The Save to DAM extension saves the Amazon product you are viewing into a site's product library.
        Load <code>extension/</code> unpacked in Chrome, then paste a token into its options.
        A token can only use the extension and product endpoints.
      </p>

      <Alert type="error" onDismiss={() => setError(null)}>{error}</Alert>

      {created && (
        <Alert type="success" onDismiss={() => setCreated(null)}>
          <strong style={{ display: 'block', marginBottom: '8px' }}>
            Token created. Copy it now; it is not stored and won't be shown again.
          </strong>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <code
              className="mono"
              style={{
                flex: 1, padding: '8px 10px', backgroundColor: 'var(--color-surface)',
                border: '1px solid var(--color-border)', borderRadius: 'var(--radius)',
                wordBreak: 'break-all', color: 'var(--color-text)', userSelect: 'all',
              }}
            >
              {created}
            </code>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => copy(created)}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </Alert>
      )}

      <div className="toolbar">
        <input
          type="text"
          className="input grow"
          value={label}
          maxLength={80}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          placeholder="Label (e.g. alton laptop)"
          aria-label="Token label"
        />
        <button type="button" className="btn btn-primary" onClick={handleCreate} disabled={creating}>
          {creating ? 'Creating…' : 'Create extension token'}
        </button>
      </div>

      {tokens.length > 0 ? (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Label</th>
                <th>Owner</th>
                <th>Created</th>
                <th>Last used</th>
                <th className="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((t) => (
                <tr key={t.id}>
                  <td style={{ fontWeight: 500 }}>{t.label}</td>
                  <td className="muted small">{t.owner_email || '—'}</td>
                  <td className="muted small">{t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}</td>
                  <td className="muted small">{t.last_used ? new Date(t.last_used).toLocaleString() : 'Never'}</td>
                  <td className="actions">
                    <button type="button" className="btn btn-danger btn-sm" onClick={() => setPendingRevoke(t)}>
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="muted small" style={{ margin: 0 }}>No extension tokens yet.</p>
      )}

      <ConfirmDialog
        open={!!pendingRevoke}
        title="Revoke extension token"
        message={`Revoke "${pendingRevoke?.label || ''}"? The extension using it stops working at once.`}
        confirmLabel="Revoke"
        danger
        onConfirm={handleRevoke}
        onCancel={() => setPendingRevoke(null)}
      />
    </div>
  );
};

export default ExtensionTokensPanel;
